
import PerfectScrollbar from "perfect-scrollbar";
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { Getter, Action, namespace } from "vuex-class";
import { TaskType, QueryNotificationServers } from "@/store/tasks/types";
import { ShareWithPerson, EventDetails } from "@/store/site-monitor/types";
import { DeviceDetails, DeviceService } from "@sureview/v2-cameras";
import { formatDateMixin } from "@/mixins";
import Multiselect from "vue-multiselect";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, numeric } from "vuelidate/lib/validators";
import { GroupAndTermQuery } from "@/store/types"
import vSelect from "vue-select";
import vselect3 from "vselect3";
import { maxData, isInternalSearch } from "@/mixins/vselect3Mixin"
import { debounce } from "lodash";
import { tasks } from '@/store/tasks';
import { NotificationContacts } from "@/store/tasks/types";

const Tasks = namespace("tasks");
const SiteMonitor = namespace("siteMonitor");
const SessionStore = namespace("sessions");

const { displayDateFromISOString, convertUtcDateTimeToLocalTimeString } = formatDateMixin.methods;

@Component({
	components: {
		multiselect: Multiselect,
		VuePerfectScrollbar: VuePerfectScrollbar,
		"v-select": vSelect,
		vselect3
	},
	mixins: [validationMixin],
	validations: {
		template: {
			subject: {
				required
			},
			message: {
				required
			}
		},
		totalUsers: {
			required,
			minLength: minLength(1)
			// name: {
			// 	required
			// },
			// email: {
			// 	required
			// },
			// telephone: {
			// 	required
			// }
		}
	}
})
export default class NotificationTask extends Vue {
	@SessionStore.Getter getSession: any;
	@SessionStore.Action updateSession: any;
	@SiteMonitor.Action eventShareSearch: any;
	@SiteMonitor.Action createAuditRecord: any;
	@SiteMonitor.Getter shareWithPersons: ShareWithPerson[];
	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
	@Tasks.Action fetchTemplateDetails: (templateId: number) => Promise<any>;

	@Tasks.Action fetchNotificationServers: () => Promise<void>;
	@Tasks.Action getNotificationServersByName: (query: string) => Promise<void>;
	@Tasks.Action fetchNotificationTemplates: any;
	@Tasks.Action fetchNotificationContacts: (item: GroupAndTermQuery) => Promise<void>;
	@Tasks.Action fetchNotificationTemplatesByTitle: (item: QueryNotificationServers) => Promise<void>;
	@Tasks.State NotificationServers: any[];
	@Tasks.State NotificationTemplates: any[];
	@Tasks.State NotificationContacts: NotificationContacts[];

	@Prop(String) taskText: string;
	@Prop(Object) taskData: object;
	@Prop(String) result: string;

	public isSearching: boolean = false;
	public isSending: boolean = false;
	public showAdvanced: boolean = false;
	public showUserSearch: boolean = false;
	public selectedUsers: any[] = [];
	public serverId: number = null;
	public templateId: number = null;
	public defaultTemplateId: number = null;
	public notificationName: string = null;
	public template: any = {};
	public templateUsers: any[] = [];

	/** Mixin helpers **/
	// Calls maxData Mixtin method
	private maxData = (data: any[]): any[] => { return maxData(data); }
	// Calls isInternalSearch Mixtin method
	private isInternalSearch = (data: any[]): boolean => { return isInternalSearch(data); }

	private async loadTemplateDetails(value: number | any = null, valueChange: boolean = false){		
		if (!value && valueChange) {
			this.clearTemplatesChildren(value);
		}

		if (this.templateId) {
			this.template = await this.fetchTemplateDetails(this.templateId);

			this.templateUsers = this.template.users.map(x => ({
				userID: x.userID,
				name: x.fullName,
				email: x.email,
				telephone: x.telephone,
				externalID: x.externalID,
				default: true
			}));
		}
	}

	/**
	 * Loads notification templates for the currently selected server.
	 */
	private async loadTemplatesForServer(serverId: number | any = null, valueChange: boolean = false) {
		if(!serverId && valueChange) this.clearServersChildren(serverId);
		if (this.serverId > 0) {
			await this.fetchNotificationTemplates(
				this.serverId
			);
		}
	}

	private async showAdvancedMode() {
		this.showAdvanced = true;
		await this.fetchNotificationServers();
		const payload: GroupAndTermQuery = {
			groupId: this.eventDetails.groupID,
			searchTerm: ""
		}
		await this.fetchNotificationContacts(payload);
		await this.loadTemplatesForServer();
	}

	get totalUsers() {
		return this.templateUsers.concat(this.selectedUsers);
	}

	get searchedUsers() {
		return this.shareWithPersons.filter(x => !this.templateUsers.map(y => y.userID).includes(x.userID));
	}

	private async eventShareFind(query: any) {
		this.isSearching = true;
		const payload: GroupAndTermQuery = {
			groupId: this.eventDetails.groupID,
			searchTerm: query
		}
		await this.fetchNotificationContacts(payload);
		this.isSearching = false;
	}

	private replaceKeywords(message: string) {
		return message
			.replace("{SiteName}", this.eventDetails.siteTitle)
			.replace("{EventName}", this.eventDetails.eventTitle)
			.replace("{EventTime}", displayDateFromISOString(this.eventDetails.created))
			.replace("{EventID}", this.eventDetails.eventID.toString());
	}

	private async setTemplate(templateId: number) {
		this.template = await this.fetchTemplateDetails(templateId);
		this.templateUsers = this.template.users.map(x => ({
			userID: x.userID,
			name: x.fullName,
			email: x.email,
			telephone: x.telephone,
			externalID: x.externalID,
			default: true
		}));
	}

	private async sendDefaultNotification() {
		if (this.defaultTemplateId) {
			this.setTemplate(this.defaultTemplateId);
		}
		await this.sendNotification();
	}

	private async sendNotification() {
		this.$v.$reset();
		this.$v.$touch();

		if (!this.isSending && !this.$v.template.$error && !this.$v.totalUsers.$error) {
			this.isSending = true;
			let deviceService = new DeviceService(this.eventDetails.devicesEndpoint);
			let authSessionID = this.getSession("3");
			let recipients = [];

			this.totalUsers.forEach((user: any) => {
				recipients.push({
					Fullname: user.name,
					ExternalID: user.externalID,
					Email: user.email,
					Telephone: user.telephone
				});
			});

			const message = {
				Content: this.replaceKeywords(this.template.message),
				Subject: this.replaceKeywords(this.template.subject),
				Parameters: this.template.parameters
			};

			try {
				await this.createAuditRecord({
					eventId: this.eventDetails.eventID,
					eventRecordTypeId: 41,
					details: `Sending notification to Server with ID ${this.serverId}`
				});

				await deviceService.sendNotification(authSessionID, this.serverId, recipients, message);

				this.$emit("complete");

				await this.createAuditRecord({
					eventId: this.eventDetails.eventID,
					eventRecordTypeId: 41,
					details: `Completed sending notification to Server with ID ${this.serverId}`
				});
			} catch (error) {
				console.error(error);
				alert("There has been an error sending this notification. Please try again later or contact support.");
				await this.createAuditRecord({
					eventId: this.eventDetails.eventID,
					eventRecordTypeId: 41,
					details: `Failed sending notification to Server with ID ${this.serverId}`
				});
			}

			this.isSending = false;
		}
	}

	private created() {
		this.updateSession({ resourceId: 3 }); // magic number idk why
	}

	private async mounted() {
		if (this.taskData) {
			if (Object.keys(this.taskData).length === 3) {
				this.notificationName = this.taskData["title"];
				this.serverId = this.taskData["serverId"];
				this.templateId = this.taskData["templateId"];
				this.defaultTemplateId = this.templateId;
			}
		}

		if (this.templateId) {
			this.setTemplate(this.templateId);
		}
	}

	/**
	 * debounce method to handle our waiting time when user is inputting data
	 * @param query Data that the user has inserted
	 * @param loading callback method to enable / disable loading icon
	*/
	private debounceUsersQuery = debounce(async (query: string, loading: any) =>  await this.fetchQueriedUsers(query, loading), 500);

	/**
	 * Method to query the users by user input
	 * @param query value inputted by the user
	 * @param loading callback method from vselect 3 to enable/disable spinning icon
	*/
	private async fetchQueriedUsers(query: string, loading: any): Promise<void> {
		// We want to check if want to query internally or through our API
		//if (isInternalSearch(this.contacts)) return;
		loading(true);
		const payload: GroupAndTermQuery = {
			groupId: this.eventDetails.groupID,
			searchTerm: query
		}
		await this.fetchNotificationContacts(payload);
		loading(false);
	}

	/**
	 * debounce method to handle our waiting time when user is inputting data
	 * @param query Data that the user has inserted
	 * @param loading callback method to enable / disable loading icon
	*/
	private debounceServersQuery = debounce(async (query: string, loading: any) =>  await this.fetchQueriedServers(query, loading), 500);

	/**
	 * Method to query the users by user input
	 * @param query value inputted by the user
	 * @param loading callback method from vselect 3 to enable/disable spinning icon
	*/
	private async fetchQueriedServers(query: string, loading: any): Promise<void> {
		// We want to check if want to query internally or through our API
		//if (isInternalSearch(this.servers)) return;
		loading(true);
		await this.getNotificationServersByName(query);
		loading(false);
	}


	/**
	 * debounce method to handle our waiting time when user is inputting data
	 * @param query Data that the user has inserted
	 * @param loading callback method to enable / disable loading icon
	*/
	private debounceTemplateQuery = debounce(async (query: string, loading: any) =>  await this.fetchQueriedTemplates(query, loading), 500);

	/**
	 * Method to query the users by user input
	 * @param query value inputted by the user
	 * @param loading callback method from vselect 3 to enable/disable spinning icon
	*/
	private async fetchQueriedTemplates(query: string, loading: any): Promise<void> {
		// We want to check if want to query internally or through our API
		//if (isInternalSearch(this.templates)) return;
		loading(true);
		const payload: QueryNotificationServers = {
			serverId: this.serverId,
			searchTerm: query
		}
		await this.fetchNotificationTemplatesByTitle(payload);
		loading(false);
	}

	/**
	 * Method to clear out the current value and any nested properties
	 * @param value expected value.
	 */
	private clearTemplatesChildren(value: any) {
		if (!value) {
			this.selectedUsers = null;
		}
	}

	/**
	 * Method to clear out the current value and any nested properties
	 * @param value expected value.
	 */
	private clearServersChildren(value: any) {
		if (!value) {
			this.templateId = null;
			this.selectedUsers = null;
		}
	}
}
